import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  AlertTriangle, 
  CheckCircle2, 
  Info, 
  LogOut, 
  HelpCircle,
  X
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

export interface UnifiedConfirmDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  type?: 'question' | 'danger' | 'warning' | 'success' | 'info' | 'logout';
  loading?: boolean;
}

const UnifiedConfirmDialog: React.FC<UnifiedConfirmDialogProps> = ({
  open,
  onClose,
  onConfirm,
  title,
  message,
  confirmText,
  cancelText,
  type = 'question',
  loading = false,
}) => {
  const { t } = useTranslation();
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    if (open) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);

  if (!mounted) return null;

  const getIcon = () => {
    switch (type) {
      case 'warning':
      case 'danger':
        return <AlertTriangle className="h-7 w-7 text-red-500" />;
      case 'success':
        return <CheckCircle2 className="h-7 w-7 text-emerald-500" />;
      case 'info':
        return <Info className="h-7 w-7 text-blue-500" />;
      case 'logout':
        return <LogOut className="h-7 w-7 text-orange-500" />;
      case 'question':
      default:
        return <HelpCircle className="h-7 w-7 text-orange-500" />;
    }
  };

  const getIconBg = () => {
    switch (type) {
      case 'warning':
      case 'danger':
        return 'bg-red-50';
      case 'logout':
      case 'question':
        return 'bg-orange-50';
      case 'success':
        return 'bg-emerald-50';
      case 'info':
        return 'bg-blue-50';
      default:
        return 'bg-orange-50';
    }
  };

  const getConfirmBtnClass = () => {
    // Force brand orange for all types on the patient side
    return 'bg-gradient-primary';
  };

  const dialogContent = (
    <AnimatePresence>
      {open && (
        <div 
          className="fixed inset-0 z-[99999] flex items-center justify-center p-4 sm:p-6"
          style={{ 
            position: 'fixed', 
            top: 0, 
            left: 0, 
            right: 0, 
            bottom: 0, 
            width: '100vw', 
            height: '100dvh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={!loading ? onClose : undefined}
            className="absolute inset-0 bg-black/60 backdrop-blur-md"
            style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0 }}
          />

          {/* Dialog Body */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            className="relative w-full max-w-[400px] overflow-hidden rounded-[2.5rem] bg-white p-8 sm:p-10 shadow-2xl text-center"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close button (optional but good for UX) */}
            {!loading && (
              <button 
                onClick={onClose}
                className="absolute top-6 right-6 p-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            )}

            <div className={`mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full ${getIconBg()}`}>
              {getIcon()}
            </div>
            
            <h3 className="mb-3 text-xl font-bold text-gray-900 leading-tight px-2">
              {title}
            </h3>
            
            <div className="mb-8 text-sm text-gray-500 leading-relaxed whitespace-pre-wrap px-2">
              {message}
            </div>

            <div className="flex flex-col gap-3">
              <button
                onClick={onConfirm}
                disabled={loading}
                className={`w-full rounded-2xl py-4 text-sm font-bold text-white shadow-md transition-all active:scale-[0.98] flex items-center justify-center gap-2 ${getConfirmBtnClass()} disabled:opacity-50`}
              >
                {loading && (
                  <svg className="animate-spin h-4 w-4 text-white" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                )}
                {confirmText || t('common.ok') || t('common.confirm') || 'OK'}
              </button>
              <button
                onClick={onClose}
                disabled={loading}
                className="w-full rounded-2xl border border-gray-100 bg-gray-50 py-4 text-sm font-bold text-gray-500 transition-all hover:bg-gray-100 active:scale-[0.98] disabled:opacity-50"
              >
                {cancelText || t('common.cancel') || 'Cancel'}
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );

  return createPortal(dialogContent, document.body);
};

export default UnifiedConfirmDialog;
